import {useLocation} from 'react-router-dom';
import {PATHS} from 'routes/constants';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {useCheckoutSettings} from 'pages/Checkout/hooks/useCheckoutSettings';
import {VelaScene} from '../engine/types';
import {useChatVerifyStore} from '../hooks/useChatVerifyStore';
import {
  isRegistrationInFlight,
  useRegistrationResultStore,
} from 'components/RegistrationResult/useRegistrationResultStore';

const SEGMENT_TO_SCENE: Record<string, VelaScene> = {
  [PATHS.verification.documentSelection]: VelaScene.IVDocumentSelection,
  [PATHS.verification.front]: VelaScene.IVFront,
  [PATHS.verification.back]: VelaScene.IVBack,
  [PATHS.verification.selfie]: VelaScene.IVSelfie,
  [PATHS.verification.contacts]: VelaScene.IVContacts,
  [PATHS.verification.qrScan]: VelaScene.IVQRScan,
};

const GUIDEBOOK_DETAIL_PATHS = [PATHS.guidebook, PATHS.guidebookPdf, PATHS.guidebookHtml];

export function useVelaScene(): VelaScene | null {
  const {pathname, search} = useLocation();
  const {isIVOnPropertyUnavailable} = useIVOnProperty();
  const {isCheckoutDate} = useCheckoutSettings();
  const chatGate = useChatVerifyStore(state => state.gate);
  const registrationPhase = useRegistrationResultStore(state => state.phase);

  if (isIVOnPropertyUnavailable && pathname.includes(`/${PATHS.verification.main}`)) {
    return VelaScene.IVUnavailable;
  }

  if (pathname.includes(`/${PATHS.addPersonalDataForm}`)) {
    if (isRegistrationInFlight(registrationPhase)) return VelaScene.GuestRegistering;
    if (registrationPhase === 'registered') return VelaScene.GuestRegistered;
    if (registrationPhase === 'failed') return VelaScene.GuestRegistrationFailed;
    return VelaScene.GuestForm;
  }

  if (pathname.includes(`/${PATHS.guestRegistration}`)) {
    return VelaScene.GuestRegistration;
  }

  if (pathname.includes(`/${PATHS.remoteAccess}`)) {
    return VelaScene.RemoteAccess;
  }

  if (pathname.includes(`/${PATHS.taxes.main}`)) {
    return VelaScene.Taxes;
  }

  if (pathname.includes(`/${PATHS.recommendations.requestsFull}`)) {
    return VelaScene.UpsellingRequests;
  }

  if (pathname.includes(`/${PATHS.recommendations.main}/`)) {
    return VelaScene.UpsellingOffer;
  }

  if (pathname.includes(`/${PATHS.recommendations.main}`)) {
    return VelaScene.Upselling;
  }

  if (pathname.includes(`/${PATHS.chat}`)) {
    if (chatGate === 'disabled') return VelaScene.ChatDisabled;
    if (chatGate === 'notRegistered') return VelaScene.ChatNotRegistered;
    if (chatGate === 'error') return VelaScene.ChatVerifyError;
    if (chatGate === 'verifying') return VelaScene.ChatVerify;
    return VelaScene.Chat;
  }

  if (pathname.includes(`/${PATHS.travelGuide}`)) {
    return new URLSearchParams(search).get('step') === 'guide'
      ? VelaScene.TravelGuide
      : null;
  }

  if (pathname.includes(`/${PATHS.esim.root}`)) {
    return VelaScene.Esim;
  }

  if (pathname.includes(`/${PATHS.deposits}`)) {
    return VelaScene.Deposit;
  }

  if (pathname.includes(`/${PATHS.checkout.rateYourStay}`)) {
    return VelaScene.CheckoutRate;
  }

  if (pathname.includes(`/${PATHS.checkout.thanks}`)) {
    return null;
  }

  if (pathname.includes(`/${PATHS.checkout.main}`)) {
    return isCheckoutDate ? VelaScene.Checkout : null;
  }

  if (pathname.includes(`/${PATHS.payments.main}/${PATHS.payments.ordersHistory}`)) {
    return VelaScene.OrderHistory;
  }

  if (pathname.includes(`/${PATHS.payments.main}`)) {
    return VelaScene.Payments;
  }

  const isGuestGuidebook = !pathname.includes(`/${PATHS.housingGuidebooks}/`);
  if (
    isGuestGuidebook &&
    GUIDEBOOK_DETAIL_PATHS.some(path => pathname.includes(`/${path}/`))
  ) {
    return VelaScene.GuidebookDetail;
  }

  const segment = pathname.split('/').filter(Boolean).pop() ?? '';
  if (segment in SEGMENT_TO_SCENE) {
    return SEGMENT_TO_SCENE[segment];
  }

  if (segment === PATHS.verification.main) {
    return VelaScene.IVStart;
  }

  return null;
}
